import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function FAQPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mykinesiologue.ma';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      question: "Qu'est-ce que la kinésiologie, concrètement ?",
      answer: "La kinésiologie est une approche qui utilise le test musculaire pour dialoguer avec le corps et repérer des zones de stress ou de surcharge. Elle mobilise ensuite des techniques guidées par ce que le corps indique comme pertinent."
    },
    {
      question: "Faut-il parler pendant une séance ?",
      answer: "La séance peut être verbale ou non verbale. Certaines personnes parlent beaucoup, d'autres très peu. L'approche s'adapte au besoin du moment."
    },
    {
      question: "Combien de séances sont nécessaires ?",
      answer: "Il n'y a pas de nombre prédéfini. Selon les situations, une séance peut déjà apporter une évolution perceptible, une amélioration, ou une compréhension plus claire. Dans d'autres cas, un accompagnement plus progressif peut être utile."
    },
    {
      question: "Est-ce une pratique médicale ?",
      answer: "Non. La kinésiologie ne pose pas de diagnostic et ne remplace pas un suivi médical. Elle peut s'inscrire en complément, dans une démarche d'accompagnement et de prévention du stress."
    },
    {
      question: "Qu'est-ce qui se passe après une séance ?",
      answer: "Les effets varient d'une personne à l'autre. Certains ressentent un relâchement, une clarté mentale ou une nouvelle disponibilité intérieure rapidement ; d'autres observent des évolutions plus progressives. L'objectif est de soutenir des ajustements durables."
    },
    {
      question: "Est-ce adapté aux personnes très rationnelles ?",
      answer: "Oui. L'approche reste structurée et guidée par le test musculaire. La séance n'exige pas de \"croire\", mais d'observer ce qui se passe dans le corps et ce qui devient possible."
    },
    {
      question: "Comment prendre rendez-vous ?",
      answer: "Par message privé ou WhatsApp. Quelques mots suffisent : prénom + ce qui vous amène en une phrase."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${siteUrl}/faq#faqpage`,
    "url": `${siteUrl}/faq`,
    "name": "FAQ - Questions fréquentes sur la kinésiologie",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-[#F7F3EE]">
      <SEO
        title="FAQ - Questions fréquentes sur la kinésiologie | MyKinesiologue Marrakech"
        description="Tout ce que vous devez savoir sur la kinésiologie et mes accompagnements. Réponses aux questions fréquentes sur les séances, les techniques et les résultats."
        keywords="FAQ kinésiologie, questions kinésiologie, séances kinésiologie, kinésiologue Marrakech questions"
        canonical={`${siteUrl}/faq`}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1120px] mx-auto px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/48f019ad192905b3f4ede24b194a300b/9e4b5e4e7afefada3563c5975e98200f.png" alt="MyKinesiologue" className="h-8 md:h-12" />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#1F1F1F] cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} style={{ fontSize: '24px' }}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Accueil</Link>
            <Link to="/approche" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Approche</Link>
            <Link to="/a-qui-sadresse" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>À qui s'adresse</Link>
            <Link to="/presence" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Présence</Link>
            <Link to="/contenus-multimedia" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Contenus multimédia</Link>
            <Link to="/prendre-rendez-vous" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Prendre rendez-vous</Link>
            <Link to="/faq" className="text-[#1F4D45] font-semibold transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>FAQ</Link>
            <Link to="/prendre-rendez-vous#contact" className="bg-[#1F4D45] text-[#F7F3EE] px-6 h-11 rounded-2xl flex items-center justify-center whitespace-nowrap hover:bg-[#1F4D45]/90 hover:translate-y-[-1px] transition-all duration-300 cursor-pointer ml-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: '500' }}>Me contacter</Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#C9C1B8]">
            <div className="px-4 py-6 flex flex-col gap-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Accueil</Link>
              <Link to="/approche" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Approche</Link>
              <Link to="/a-qui-sadresse" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>À qui s'adresse</Link>
              <Link to="/presence" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Présence</Link>
              <Link to="/contenus-multimedia" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Contenus multimédia</Link>
              <Link to="/prendre-rendez-vous" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Prendre rendez-vous</Link>
              <Link to="/faq" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F4D45] font-medium transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>FAQ</Link>
              <Link to="/prendre-rendez-vous#contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-[#1F4D45] text-[#F7F3EE] px-6 h-12 rounded-2xl flex items-center justify-center whitespace-nowrap cursor-pointer mt-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Me contacter</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 md:pt-48 pb-16 md:pb-32 bg-[#F7F3EE] relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b89ed3fe197176630a61a3d7345d0a51.jpg" 
            alt="" 
            className="w-full h-full object-cover object-center opacity-30"
          />
        </div>
        <div className="max-w-[1120px] mx-auto px-4 md:px-8 text-center relative z-10">
          <div className="inline-block border border-[#C9C1B8] rounded-full px-4 py-2 mb-6 md:mb-8 bg-[#F7F3EE]/80 backdrop-blur-sm">
            <span className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>Questions fréquentes</span>
          </div>
          <h1 className="text-[#1F1F1F] mb-4 md:mb-6 max-w-3xl mx-auto" style={{ fontFamily: 'Fraunces, serif', fontSize: '50px', lineHeight: '1.1', fontWeight: '600' }}>
            Vos questions, mes réponses
          </h1>
          <p className="text-[#5E5A55] max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
            Tout ce que vous devez savoir sur la kinésiologie et mes accompagnements
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-32 bg-[#F7F3EE]">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white border border-[#C9C1B8] rounded-2xl overflow-hidden transition-all duration-400"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 md:px-12 py-6 md:py-8 flex items-center justify-between text-left hover:bg-[#F7F3EE]/50 transition-colors duration-400 cursor-pointer"
                >
                  <h3 className="text-[#1F1F1F] pr-4 md:pr-8" style={{ fontFamily: 'Fraunces, serif', fontSize: '22px', lineHeight: '1.3', fontWeight: '600' }}>
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i 
                      className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-[#1F1F1F] transition-transform duration-400`} 
                      style={{ fontSize: '24px' }}
                    ></i>
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-400 ${openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-6 md:px-12 pb-6 md:pb-8">
                    <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.6' }}>
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-[#1F1F1F] mb-4" style={{ fontFamily: 'Fraunces, serif', fontSize: '35px', lineHeight: '1.2', fontWeight: '600' }}>
              Vous avez d'autres questions ?
            </h2>
            <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
              N'hésitez pas à me contacter directement
            </p>
          </div>
          <div className="flex items-center justify-center">
            <a href="mailto:tatiana@mykunesiologue.com" className="inline-flex items-center gap-3 bg-[#1F4D45] text-[#F7F3EE] px-6 md:px-8 h-12 rounded-2xl whitespace-nowrap hover:translate-y-[-2px] transition-all duration-400 cursor-pointer" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
              <i className="ri-mail-line"></i>
              Me contacter
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F4D45] py-12 md:py-16">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <Link to="/" className="inline-block mb-4">
                <img src="https://static.readdy.ai/image/48f019ad192905b3f4ede24b194a300b/9e4b5e4e7afefada3563c5975e98200f.png" alt="MyKinesiologue" className="h-10" />
              </Link>
              <p className="text-[#E9E0D4]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                Kinésiologie à Marrakech
              </p>
            </div>
            <div>
              <h3 className="text-[#E9E0D4] uppercase tracking-wider mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '1.5px' }}>
                Navigation
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <Link to="/" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Accueil</Link>
                <Link to="/contenus-multimedia" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Contenus multimédia</Link>
                <Link to="/approche" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Approche</Link>
                <Link to="/prendre-rendez-vous" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Prendre rendez-vous</Link>
                <Link to="/a-qui-sadresse" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>À qui s'adresse</Link>
                <Link to="/faq" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>FAQ</Link>
                <Link to="/presence" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Présence</Link>
              </div>
            </div>
            <div>
              <h3 className="text-[#E9E0D4] uppercase tracking-wider mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '1.5px' }}>
                Contact
              </h3>
              <div className="flex flex-col gap-2">
                <a href="mailto:tatiana@mykunesiologue.com" className="text-[#F7F3EE] hover:underline break-all" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>tatiana@mykunesiologue.com</a>
                <a href="tel:+2120776664292" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>+212 0776664292</a>
                <p className="text-[#E9E0D4] mt-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  Marrakech, Maroc
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-[#F7F3EE]/20 pt-6 text-center">
            <p className="text-[#E9E0D4]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
              © 2025 MyKinesiologue. Tous droits réservés. · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:underline">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
